'use client';

import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import styled from '@emotion/styled';
import { ButtonSize, ButtonType } from 'constants/button';
import { ModalParams, ModalType } from 'constants/modal';
import { useCallback, useEffect, useState } from 'react';
import { Button } from 'ui-components/common/Button';
import { ModalLayout } from 'ui-components/layout/ModalLayout';
import { useShowModal } from 'ui-hooks/useShowModal';
import { ModalHandler } from 'utils/ModalHandler';

export const ConfirmModal = () => {
  const { isShow, revealModal, showModal, hideModal } = useShowModal();
  const [state, setState] = useState<ModalParams[ModalType.Confirm]>();

  const handleClose = useCallback(() => {
    state?.onCancel?.();
    hideModal();
  }, [hideModal, state]);

  const handleConfirm = useCallback(() => {
    state?.onConfirm?.();
    hideModal();
  }, [hideModal, state]);

  useEffect(() => {
    const listener = (show: boolean, params?: ModalParams[ModalType.Confirm]) => {
      if (show) {
        showModal();
      } else {
        hideModal();
      }
      setState(params);
    };

    ModalHandler.addEventListener(ModalType.Confirm, listener);

    return () => {
      ModalHandler.removeEventListener(ModalType.Confirm);
    };
  }, [hideModal, showModal]);

  return (
    <ModalLayout isShow={isShow} revealModal={revealModal}>
      <StyledModal>
        <StyledTitle>{state?.title}</StyledTitle>
        <StyledDescription>{state?.description}</StyledDescription>
        <StyledButtons>
          <StyledButton
            label={state?.confirmText || '확인'}
            size={ButtonSize.Small}
            buttonTypography={Typography.XXXSmall}
            onClick={handleConfirm}
          />
          <StyledButton
            label={state?.cancelText || '닫기'}
            size={ButtonSize.Small}
            buttonTypography={Typography.XXXSmall}
            buttonType={ButtonType.SecondaryNoBorder}
            onClick={handleClose}
          />
        </StyledButtons>
      </StyledModal>
    </ModalLayout>
  );
};

const StyledModal = styled.div`
  width: calc(100vw - 60px);
  max-width: 320px;
  background-color: ${Color.White};
  padding: 24px;
  border-radius: 4px;
  box-shadow:
    0 4px 8px -3px rgba(0, 0, 0, 0.2),
    0 16px 32px -3px rgba(0, 0, 0, 0.2);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledTitle = styled.p`
  margin-bottom: 16px;
  text-align: left;
  font-weight: ${FontWeight.Bold};
  ${Typography.Small};
  color: ${Color.Black};
  word-break: keep-all;
`;

const StyledDescription = styled.p`
  color: ${Color.Gray700};
  text-align: left;
  margin-bottom: 24px;
  font-weight: ${FontWeight.Medium};
  ${Typography.XXSmall};
  word-break: break-all;
  white-space: pre-line;
`;

const StyledButton = styled(Button)`
  padding: 0 20px;
  height: 40px;
  width: 100%;
`;

const StyledButtons = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
`;
